import { Popover } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CgChevronDown } from 'react-icons/cg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IHaggleFilters } from '@/interfaces/api/product/IHaggleFilters';
import { haggleApiService } from '@/services/haggle.service';

interface HaggleStoredFiltersProps {
	restrictVendors?: boolean;
}

export enum HaggleStatus {
	ALL = 'all',
	LIVE = 'live',
	UPCOMING = 'upcoming',
}

export interface StoredFilterState {
	vendors: string[];
	productTypes: string[];
	priceRange: {
		min: number;
		max: number;
	};
	status: HaggleStatus;
	countries: string[];
}

const DEFAULT_FILTERS: StoredFilterState = {
	vendors: [],
	productTypes: [],
	priceRange: { min: 0, max: 10000 },
	status: HaggleStatus.ALL,
	countries: [],
};

export const HaggleStoredFilters = ({
	restrictVendors = false,
}: HaggleStoredFiltersProps) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [filters, setFilters] = useState<StoredFilterState>(DEFAULT_FILTERS);

	// Fetch stored filters from backend
	const { data: storedFilters } = useQuery<IHaggleFilters>({
		queryKey: [ApiQueryKeys.getAllHaggleStoredFilters],
		queryFn: () => haggleApiService.getAllHaggleStoredFilters(),
		refetchOnWindowFocus: false,
	});

	// Initialize filters from URL params
	useEffect(() => {
		const vendors = searchParams.getAll('vendor');
		const productTypes = searchParams.getAll('type');
		const countries = searchParams.getAll('country');
		const status =
			(searchParams.get('status') as HaggleStatus) || HaggleStatus.ALL;
		const minPrice = Number(searchParams.get('minPrice')) || 0;
		const maxPrice = Number(searchParams.get('maxPrice')) || 10000;

		setFilters({
			vendors,
			productTypes,
			countries,
			status,
			priceRange: { min: minPrice, max: maxPrice },
		});
	}, [searchParams]);

	const haggleStatusOptions = [
		{ value: 'all', label: 'All' },
		{ value: 'live', label: 'Live' },
		{ value: 'upcoming', label: 'Coming Soon' },
	];

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleFilterChange = (type: keyof StoredFilterState, value: any) => {
		setFilters((prev) => ({ ...prev, [type]: value }));
	};

	const handleSearch = () => {
		const params = new URLSearchParams();

		if (filters.priceRange.min > filters.priceRange.max) {
			toast.error('Minimum price cannot be greater than maximum price.');
			return;
		}

		// Add vendors to URL
		filters.vendors.forEach((vendor) => {
			params.append('vendor', vendor);
		});

		// Add product types to URL
		filters.productTypes.forEach((type) => {
			params.append('type', type);
		});

		// Add countries to URL
		filters.countries.forEach((country) => {
			params.append('country', country);
		});

		// Add status if not 'all'
		if (filters.status !== HaggleStatus.ALL) {
			params.append('status', filters.status);
		}

		// Add price range if not default
		if (filters.priceRange.min !== 0) {
			params.append('minPrice', filters.priceRange.min.toString());
		}
		if (filters.priceRange.max !== 10000) {
			params.append('maxPrice', filters.priceRange.max.toString());
		}

		navigate(`?${params.toString()}`);
	};

	const handleResetFilters = () => {
		// Reset filters to default values
		setFilters(DEFAULT_FILTERS);
		// Clear URL parameters
		navigate('');
	};

	const renderSelectedFilters = (type: keyof StoredFilterState) => {
		switch (type) {
			case 'status':
				return filters.status !== HaggleStatus.ALL ? filters.status : '';
			case 'vendors':
				return filters.vendors.length > 0 ? filters.vendors.join(', ') : '';
			case 'productTypes':
				return filters.productTypes.length > 0
					? filters.productTypes.join(', ')
					: '';
			case 'priceRange':
				if (filters.priceRange.min === 0 && filters.priceRange.max === 10000) {
					return '';
				}
				return `$${filters.priceRange.min} - $${filters.priceRange.max}`;
			case 'countries':
				return filters.countries.length > 0 ? filters.countries.join(', ') : '';
			default:
				return '';
		}
	};

	return (
		<div className="w-full">
			<div className="bg-white rounded-xl shadow-lg p-4">
				<div className="flex gap-2 items-center justify-between flex-wrap">
					<div className="flex gap-2 items-center flex-wrap z-30">
						{/* Product Types Filter */}
						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Category</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.productTypes.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('productTypes')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Category
										</div>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{storedFilters?.productTypes.map((type) => (
												<label
													key={type}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														id={type}
														checked={filters.productTypes.includes(type)}
														onChange={(e) => {
															const newTypes = e.target.checked
																? [...filters.productTypes, type]
																: filters.productTypes.filter(
																		(t) => t !== type,
																  );
															handleFilterChange('productTypes', newTypes);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">
														{type || 'Uncategorized'}
													</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						{/* Vendors Filter */}
						{!restrictVendors && (
							<Popover className="relative">
								{({ open }) => (
									<>
										<Popover.Button
											className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
												open
													? 'bg-purpleBrand-50 border-purpleBrand-900 focus:border-purpleBrand-900'
													: ''
											}`}
										>
											<div className="flex items-center gap-2">
												<span>Merchants</span>
												<CgChevronDown
													className={`w-4 h-4 transition-transform ${
														open ? 'rotate-180' : ''
													}`}
												/>
											</div>
											{filters.vendors.length > 0 && (
												<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
													{renderSelectedFilters('vendors')}
												</span>
											)}
										</Popover.Button>
										<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
											<div className="mb-3 text-sm text-gray-600">
												Filter Merchants
											</div>
											<div className="space-y-2 max-h-60 overflow-y-auto">
												{storedFilters?.vendors.map((vendor) => (
													<label
														key={vendor}
														className="flex items-center space-x-2"
													>
														<input
															type="checkbox"
															id={vendor}
															checked={filters.vendors.includes(vendor)}
															onChange={(e) => {
																const newVendors = e.target.checked
																	? [...filters.vendors, vendor]
																	: filters.vendors.filter((v) => v !== vendor);
																handleFilterChange('vendors', newVendors);
															}}
															className="rounded text-blue-600"
														/>
														<span className="text-sm">{vendor}</span>
													</label>
												))}
											</div>
										</Popover.Panel>
									</>
								)}
							</Popover>
						)}

						{/* Price Range Filter */}
						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Price Range</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{renderSelectedFilters('priceRange') && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('priceRange')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[300px]">
										<div className="mb-3 text-sm text-gray-600">
											Filter Price Range
										</div>
										<div className="flex items-center space-x-4">
											<input
												type="number"
												placeholder="Min"
												value={filters.priceRange.min}
												min="0"
												onChange={(e) =>
													handleFilterChange('priceRange', {
														...filters.priceRange,
														min: Number(e.target.value),
													})
												}
												className="w-full p-2 border rounded"
											/>
											<span>to</span>
											<input
												type="number"
												placeholder="Max"
												value={filters.priceRange.max}
												min="0"
												onChange={(e) =>
													handleFilterChange('priceRange', {
														...filters.priceRange,
														max: Number(e.target.value),
													})
												}
												className="w-full p-2 border rounded"
											/>
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						{/* Countries Filter */}
						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Region</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.countries.length > 0 && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('countries')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Region
										</div>
										<div className="space-y-2 max-h-60 overflow-y-auto">
											{storedFilters?.countryNames.map((country) => (
												<label
													key={country}
													className="flex items-center space-x-2"
												>
													<input
														type="checkbox"
														id={country}
														checked={filters.countries.includes(country)}
														onChange={(e) => {
															const newCountries = e.target.checked
																? [...filters.countries, country]
																: filters.countries.filter(
																		(c) => c !== country,
																  );
															handleFilterChange('countries', newCountries);
														}}
														className="rounded text-blue-600"
													/>
													<span className="text-sm">{country}</span>
												</label>
											))}
										</div>
									</Popover.Panel>
								</>
							)}
						</Popover>

						{/* Status Filter */}
						<Popover className="relative">
							{({ open }) => (
								<>
									<Popover.Button
										className={`px-4 py-2 border rounded-lg flex flex-col items-start gap-1 text-sm ${
											open ? 'bg-purpleBrand-50 border-purpleBrand-900' : ''
										}`}
									>
										<div className="flex items-center gap-2">
											<span>Calendar</span>
											<CgChevronDown
												className={`w-4 h-4 transition-transform ${
													open ? 'rotate-180' : ''
												}`}
											/>
										</div>
										{filters.status !== HaggleStatus.ALL && (
											<span className="text-xs bg-gray-200 px-2 py-1 rounded-full truncate max-w-[150px]">
												{renderSelectedFilters('status') ===
												HaggleStatus.UPCOMING
													? 'coming soon'
													: renderSelectedFilters('status')}
											</span>
										)}
									</Popover.Button>
									<Popover.Panel className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg p-4 w-[250px] border border-gray-200">
										<div className="mb-3 text-sm text-gray-600">
											Filter Haggle Status
										</div>
										{haggleStatusOptions.map((status) => (
											<div
												key={status.value}
												className="flex items-center gap-2 p-2"
											>
												<input
													type="radio"
													id={status.value}
													checked={filters.status === status.value}
													onChange={() =>
														handleFilterChange('status', status.value)
													}
													className="rounded text-blue-600"
												/>
												<label
													htmlFor={status.value}
													className="capitalize text-sm"
												>
													{status.label}
												</label>
											</div>
										))}
									</Popover.Panel>
								</>
							)}
						</Popover>
					</div>

					{/* Search and Reset Buttons */}
					<div className="flex gap-2 mt-2 sm:mt-0">
						<button
							onClick={handleResetFilters}
							className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
						>
							Reset
						</button>
						<button
							onClick={handleSearch}
							className="px-4 py-2 text-sm text-white rounded-lg  bg-purpleBrand-500 hover:bg-purpleBrand-600 active:bg-purpleBrand-900 transition-colors"
						>
							Search
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
